/**
 * SDMS conditional formatting — carry the template's Excel rules into Univer.
 *
 * WHY. The SDMS templates colour their QC columns with real Excel conditional
 * formatting: QCStatus "Fail" pink, "Pass" green, IsReport "True" blue / "False"
 * orange, and Rec %/RPD % green-or-pink against their control limits. `excelToUniver`
 * mapped static styles only and dropped `<conditionalFormatting>` entirely, so AI
 * rendered those cells plain while OG showed colour (user-reported 2026-08-13).
 *
 * The rules CANNOT be baked into static styles at load time: Rec %/RPD %/QCStatus are
 * formula cells, so their values only exist after Univer's engine runs. They have to
 * be real CF rules that re-evaluate.
 *
 * Rules present in template 9116 (the shapes this converter must handle):
 *   AH7      IsReport  containsText "True"/"False"            → font colour only
 *   AE7:AI7  QCStatus  containsText "Pass" / cellIs = "Fail"  → font + fill
 *   P7       RPD %     containsBlanks(stopIfTrue) · lessThan VALUE(AB7) / VALUE(AA7)
 *   N7       Rec %     containsBlanks(stopIfTrue) · between VALUE(Y7),VALUE(Z7) · lessThan
 *   D7                 cellIs = AD7
 *
 * TWO THINGS THAT MAKE THIS MORE THAN A DIRECT MAP:
 *
 * 1. ROW EXPANSION. The rules are authored against the single DETAIL row (row 7) and
 *    the mail merge repeats that row down the band. A rule left anchored on row 7
 *    would colour only the first data row, so each range is EXTENDED to the merged
 *    band and every formula operand is ROW-SHIFTED per row — the same treatment
 *    `sdmsMailMerge.shiftFormula` applies to cell formulas.
 *
 * 2. stopIfTrue. `containsBlanks → stopIfTrue` exists so an EMPTY Rec %/RPD % cell
 *    takes no colour. Univer's builder has no stop-if-true, so instead of emitting
 *    that rule we AND a non-blank guard into the rules that follow it. Dropping it
 *    would paint every blank Rec % cell pink — worse than no colour at all.
 */

/** One Excel CF rule as ExcelJS parses it (the subset we consume). */
interface XlsxCfRule {
  type?: string;
  operator?: string;
  priority?: number;
  text?: string;
  stopIfTrue?: boolean;
  formulae?: string[];
  style?: {
    font?: { color?: { argb?: string } };
    fill?: { bgColor?: { argb?: string }; fgColor?: { argb?: string } };
  };
}
interface XlsxCf { ref?: string; rules?: XlsxCfRule[] }

/** Minimal shape of Univer's CF rule builder (facade). */
interface CfBuilder {
  setRanges: (r: Array<{ startRow: number; endRow: number; startColumn: number; endColumn: number }>) => CfBuilder;
  whenFormulaSatisfied: (f: string) => CfBuilder;
  setFontColor: (c: string) => CfBuilder;
  setBackground: (c: string) => CfBuilder;
  build: () => unknown;
}
interface CfRange {
  createConditionalFormattingRule: () => CfBuilder;
  addConditionalFormattingRule: (rule: unknown) => unknown;
}

/** Log every FILL rule as it is applied (`[SDMS-CF] AH7 fill=#... <= ...`).
 *  Off now that the IsReport phantom-fill is closed (2026-08-13, user-confirmed);
 *  flip to true to re-trace which rule paints which cell. */
const SDMS_CF_TRACE = false;

/** CF rule TYPES ruleFormula() can translate. Any other type (colorScale, dataBar,
 *  iconSet, top10, aboveAverage, duplicateValues/uniqueValues, beginsWith, endsWith,
 *  notContainsText) hits `default: return null` and renders no colour. A template sweep
 *  (2026-09-18) found NONE authored in the 55 live templates, so this is forward-compat
 *  only — but the guard below WARNS if a future template introduces one, instead of the
 *  colour silently vanishing. `containsBlanks` is consumed as the no-colour guard. */
const SUPPORTED_CF_TYPES = new Set(['containsText', 'cellIs', 'expression', 'containsBlanks']);

const ARGB = (a?: string): string | null =>
  !a ? null : `#${a.length === 8 ? a.slice(2) : a}`.toUpperCase();

/** "AH7" / "AE7:AI7" → zero-based bounds. */
function parseRef(ref: string): { r1: number; c1: number; r2: number; c2: number } | null {
  const cell = (s: string) => {
    const m = /^\$?([A-Z]+)\$?(\d+)$/.exec(s.trim().toUpperCase());
    if (!m) return null;
    let c = 0;
    for (const ch of m[1]!) c = c * 26 + (ch.charCodeAt(0) - 64);
    return { r: parseInt(m[2]!, 10) - 1, c: c - 1 };
  };
  const [a, b] = ref.split(':');
  const A = cell(a ?? ''); if (!A) return null;
  const B = b ? cell(b) : A; if (!B) return null;
  return { r1: Math.min(A.r, B.r), c1: Math.min(A.c, B.c), r2: Math.max(A.r, B.r), c2: Math.max(A.c, B.c) };
}

/** An sqref is a SPACE-SEPARATED LIST of areas — "K84 M84", "AI2:AL6 AE2:AH6" — and
 *  every area shares the block's rules. parseRef() only understood ONE area and returned
 *  null for the rest, so the whole block was dropped: on HAA (8075) that silently killed
 *  421 of its blocks, and the "Yes" column lost the green LIMS shows (client 2026-08-25,
 *  QB26082001_009). 518 blocks across 6 templates are multi-area. */
function parseRefs(ref: string): Array<{ r1: number; c1: number; r2: number; c2: number }> {
  return ref.split(/\s+/).filter(Boolean).map(parseRef)
    .filter((x): x is { r1: number; c1: number; r2: number; c2: number } => x !== null);
}

/** Shift every relative A1 row reference in a formula by `k` (mirrors
 *  sdmsMailMerge.shiftFormula — absolute `$5` rows are left alone). */
function shiftRows(formula: string, k: number): string {
  if (!k) return formula;
  // A name followed by "(" is a function (LOG10), not a cell — see sdmsMailMerge.shiftFormula.
  return formula.replace(/(\$?)([A-Z]{1,3})(\$?)(\d+)(?!\d)(?!\s*\()/g, (m, ca, col, ra, row) =>
    ra ? m : `${ca}${col}${parseInt(row, 10) + k}`);
}

/** Shift relative COLUMN letters by `dc` — needed when a multi-column rule
 *  (AE7:AI7) is expanded cell-by-cell, so each cell's operands move with it.
 *  Absolute `$AE` columns are left alone, as Excel does. */
function shiftCols(formula: string, dc: number): string {
  if (!dc) return formula;
  return formula.replace(/(\$?)([A-Z]{1,3})(\$?)(\d+)(?!\d)(?!\s*\()/g, (m, ca, col, ra, row) => {
    if (ca) return m;
    let n = 0; for (const ch of col as string) n = n * 26 + (ch.charCodeAt(0) - 64);
    n += dc; if (n < 1) return m;
    let t = ''; let x = n;
    while (x > 0) { const r = (x - 1) % 26; t = String.fromCharCode(65 + r) + t; x = Math.floor((x - 1) / 26); }
    return `${t}${ra}${row}`;
  });
}

/** A1 of the rule's own anchor cell on `row`, used to build guard expressions. */
function anchorA1(col: number, row: number): string {
  let s = '', c = col + 1;
  while (c > 0) { const m = (c - 1) % 26; s = String.fromCharCode(65 + m) + s; c = Math.floor((c - 1) / 26); }
  return `${s}${row + 1}`;
}

/**
 * Translate one worksheet's CF blocks into Univer rules.
 *
 * @param cfs        ExcelJS `worksheet.conditionalFormattings`
 * @param sheet      Univer FRange provider for the target sheet
 * @param bandStart  0-based first row of the merged data band (the template's detail row)
 * @param bandEnd    0-based last row of the merged band (inclusive)
 */
export function applySdmsConditionalFormats(
  cfs: XlsxCf[] | undefined,
  sheet: CfRange,
  bandStart: number,
  bandEnd: number,
  /** The sheet's USED extent (0-based, inclusive). A CF ref is clamped to this —
   *  see the note below; without it a whole-column format alone is 1,048,576
   *  rules. Defaults are generous fallbacks for callers that don't know. */
  usedMaxRow = 4_096,
  usedMaxCol = 512,
  /** Emit one rule per CELL up to this many; above it, merge same-shape rows into one
   *  rule per column run. See the merge pass for why the split exists. Tests set it to 0
   *  to exercise the merged path on a small fixture. */
  mergeAbove = 600,
): number {
  if (!Array.isArray(cfs) || bandEnd < bandStart) return 0;
  let applied = 0;
  // Failures are counted and the FIRST one kept, so a partly-applied sheet reports
  // itself instead of looking identical to a fully-applied one.
  let failed = 0;
  let firstError: { self: string; expr: string; error: unknown } | null = null;
  /* Every rule this sheet WANTS, one entry per cell, emitted only after the merge pass
     below. Collecting first is what makes the merge possible at all. */
  const pending: Array<{ row: number; col: number; self: string; formula: string; fill: string | null; font: string | null }> = [];
  // Forward-compat guard: CF rule types this converter can't translate (colorScale/dataBar/
  // iconSet/…) render no colour — collect any seen so the warn below surfaces a future template
  // instead of the colour silently vanishing.
  const unsupportedTypesSeen = new Set<string>();

  for (const cf of cfs) {
    const areas = parseRefs(cf.ref ?? '');
    if (areas.length === 0 || !Array.isArray(cf.rules) || cf.rules.length === 0) continue;
    for (const r of cf.rules) if (r.type && !SUPPORTED_CF_TYPES.has(r.type)) unsupportedTypesSeen.add(r.type);

    /* ANCHOR — the top-left of the sqref's BOUNDING BOX, shared by every area.
       Excel writes a rule's relative references against that one origin, not against
       each area's own corner. Template 8086 proves it: sqref "AI2:AL6 AE2:AH6" carries
       NOT(ISERROR(SEARCH($P$7,AE2))), and AE2 is the bounding-box corner (the SECOND
       area's), so each cell ends up testing ITSELF once shifted. Anchoring per area
       would have made the first area test AI2 against nothing. For a single-area block
       the anchor IS its top-left, so nothing changes there. */
    const anchor = {
      r: Math.min(...areas.map(a => a.r1)),
      c: Math.min(...areas.map(a => a.c1)),
    };

    /* CLAMP THE REF TO THE USED RANGE.
       Excel stores "apply to the whole column/row" as the sheet's theoretical
       maximum — this workbook has `A6:XFD6` (16,384 columns) and
       `E1:E1048576` (1,048,576 rows). Expanding those literally asked for
       2,426,002 Univer rules on one batch (QB26081503_005): the tab went "Page
       Unresponsive" and the sheet never painted. There is nothing to paint
       outside the used range, so clamping changes no visible colour while
       turning millions of rules back into thousands.
       Measured by sdmsCfLoad.test.ts, which fails if the count climbs again. */
    // The "blank ⇒ no colour" rule. ExcelJS reports it as
    // `type:'containsText', operator:'containsBlanks'` and DROPS `stopIfTrue`
    // entirely (verified against template 9116), so it must be detected on the
    // OPERATOR — keying on type/stopIfTrue silently never matched and every blank
    // Rec %/RPD % cell came out coloured.
    const blankStop = cf.rules.some(r => r.operator === 'containsBlanks' || r.type === 'containsBlanks');

    /* ── PRIORITY: Excel picks ONE winner per cell, we used to paint the last match ──
       A cfRule carries a `priority`; when several rules match the same cell Excel
       applies them in ASCENDING priority and the first match owns the cell's look.
       We emitted them in file order and let Univer's last rule win, which is the
       opposite. Colilert-18 Presence/Absence (template 8103) puts four rules on
       Result — p1000 ="Absent" GREEN, p1001 ="Present" PINK, p1003 <>"Absent"
       ORANGE, p1008 <>"Present" ORANGE — where the two orange rules are only the
       catch-all for a value that is neither. "Absent" matches p1000 AND p1008, so
       Excel/ALPACA draw it green and we drew the whole column orange (client
       2026-08-25, QB26081914_040).
       Fixed by sorting on priority and making each rule EXCLUSIVE of the
       higher-priority ones in its own block — AND(IF(higher,FALSE,TRUE), mine) — so the
       winner is the same cell-by-cell whatever order Univer evaluates in. The
       exclusion never crosses a `<conditionalFormatting>` block, which is what
       keeps an unrelated font-only rule elsewhere on the row from suppressing a
       fill here. */
    const ordered = cf.rules
      .map((rule, seq) => ({ rule, seq }))
      .sort((a, b) =>
        (a.rule.priority ?? Number.MAX_SAFE_INTEGER) - (b.rule.priority ?? Number.MAX_SAFE_INTEGER)
        || a.seq - b.seq);

    const painters: Array<{ rule: (typeof cf.rules)[number]; fill: string | null; font: string | null }> = [];
    for (const { rule } of ordered) {
      if (rule.operator === 'containsBlanks' || rule.type === 'containsBlanks') continue;  // consumed as the guard
      const font = ARGB(rule.style?.font?.color?.argb);
      // A dxf paints a BACKGROUND only when it carries a real pattern fill.
      // A FONT-ONLY rule still surfaces a `fill` object through ExcelJS (pattern
      // 'none', and/or an fgColor that is the pattern's foreground, not a
      // background — dxf pattern fills invert fg/bg), so taking any colour we
      // could find invented a background Excel/OG never draw. That is exactly the
      // IsReport bug: its rule is font-only (cyan `True`) and we painted it green.
      // Verified 2026-08-13 — OG shows `True` unfilled; the engine itself is fine
      // (sdmsCfPredicate.engine.test.ts proves SEARCH/ISBLANK match Excel exactly,
      // so neither the column shift nor the predicate was ever at fault).
      const rawFill = rule.style?.fill as
        { pattern?: string; bgColor?: { argb?: string }; fgColor?: { argb?: string } } | undefined;
      const solid = !!rawFill && rawFill.pattern !== 'none';
      // bgColor FIRST: in a dxf patternFill the background is the visible colour.
      const fill = solid ? ARGB(rawFill.bgColor?.argb ?? rawFill.fgColor?.argb) : null;
      if (!font && !fill) continue;                      // nothing to paint
      painters.push({ rule, fill, font });
    }

    /* Collected, not emitted yet — see the merge pass after the block loop. Still one
       entry per CELL, because Excel evaluates a multi-COLUMN rule (AE7:AI7) against
       each cell's own value; anchoring a whole span on its first column is what made
       QCStatus="Pass" paint IsReport green too. */
    for (const area of areas) {
      const ref = {
        r1: area.r1,
        c1: area.c1,
        r2: Math.min(area.r2, Math.max(usedMaxRow, bandEnd)),
        c2: Math.min(area.c2, usedMaxCol),
      };
      if (ref.r2 < ref.r1 || ref.c2 < ref.c1) continue;

      // Only rules anchored ON the detail row get expanded down the band; anything
      // else (a header-only rule) keeps its own rows.
      const onBand = ref.r1 === bandStart && ref.r2 === bandStart;
      const rows = onBand
        ? Array.from({ length: bandEnd - bandStart + 1 }, (_, i) => bandStart + i)
        : Array.from({ length: ref.r2 - ref.r1 + 1 }, (_, i) => ref.r1 + i);

      for (const row of rows) {
        const k = row - anchor.r;                        // offsets run from the sqref anchor,
        for (let col = ref.c1; col <= ref.c2; col++) {   // never from this area's own corner
          const self = anchorA1(col, row);
          const beaten: string[] = [];                   // higher-priority conditions, this cell
          for (const { rule, fill, font } of painters) {
            const expr = ruleFormula(rule, self, k, col - anchor.c);
            if (!expr) continue;
            const parts: string[] = [];
            if (blankStop) parts.push(`LEN(TRIM(${self}))>0`);
          /* NEGATED WITH IF(x, FALSE, TRUE), NOT WITH NOT(x) — Univer's NOT() is broken.
             Measured against the real engine (sdmsCfNegation.engine.test.ts): NOT() over a
             comparison that involves a CELL REFERENCE and evaluates FALSE returns #VALUE!
             instead of TRUE. NOT(1=2) is fine, NOT(ISNUMBER(A2)) is fine, and NOT(A2="Present")
             is fine when the comparison is TRUE — only the reference-bearing FALSE case errors,
             the same flavour of unwrapping bug as the _LIMSRound ArrayValueObject defect.
             An erroring guard means the rule never fires, which is why "Present" came out
             UNFILLED after the priority fix landed (client 2026-08-25). IF() takes the same
             shapes and is correct in both branches. */
            for (const won of beaten) parts.push(`IF(${won}, FALSE, TRUE)`);
            parts.push(expr);
            const guarded = parts.length > 1 ? `AND(${parts.join(', ')})` : expr;
            pending.push({ row, col, self, formula: guarded, fill, font });
            beaten.push(expr);
          }
        }
      }
    }
  }
  /* ── MERGE: one rule per COLUMN RUN, not per cell ────────────────────────────────
     Emitting a rule per cell put 1,313 rules on a single Metals_ICP_OES sheet and Univer
     answered with `RangeError: Maximum call stack size exceeded` (client 2026-08-24,
     QB26082205_014, confirmed from the browser console). Every rule after the overflow
     was rejected, the per-rule catch swallowed it, and the affected columns rendered
     with no QC colour at all — "IS % Of AvgResponse is not green". Columns that still
     looked right, like Rec %, only did so because DevExpress had baked their colour into
     the CELL STYLE; their CF was just as dead.

     Univer registers a CF formula WITH ITS RANGE (`registerFormulaWithRange`) and reads
     each cell's result back by RELATIVE row/col (sheets-conditional-formatting
     index.js:1565), i.e. it shifts references per cell exactly as Excel does. So a run of
     rows that differ ONLY by that shift is one rule, and the same colours come out of
     ~15 rules instead of 1,313.

     Rows may only merge when their formulas are the SAME SHAPE — identical once every
     relative reference is expressed as an offset from that row. Two rows carrying
     genuinely different bounds canonicalise differently and stay separate rules, so a
     template with per-row limits is unaffected. Columns are never merged: a
     multi-column rule must keep evaluating against each cell's own value. */
  const canonical = (f: string, row0: number): string =>
    f.replace(/(\$?)([A-Z]{1,3})(\$?)(\d+)/g, (m, ca: string, cl: string, ra: string, r: string) =>
      ra ? m : `${ca}${cl}~${Number(r) - (row0 + 1)}`);   // A1 rows are 1-based

  /* MERGE ONLY WHEN PER-CELL WOULD BE TOO MANY.
     Per-cell rules are the PROVEN path — every sheet whose colours are known-good today
     renders through them, and they need no assumption about how Univer evaluates a rule
     across a range. They only fail at volume, where Univer's stack gives out. So keep
     them below the point where that happens and merge above it, which confines the
     merged path to sheets that could not render any other way.
     The observed overflow was at 1,313 rules; the largest KNOWN-GOOD sheet builds 420.
     600 sits between the two, well clear of both. */
  const mergeRuns = pending.length > mergeAbove;

  const runs = new Map<string, Array<{ row: number; col: number; self: string; formula: string; fill: string | null; font: string | null }>>();
  const seen = new Set<string>();
  for (const p of pending) {
    // Drop exact duplicates first: a band-expanded block and the sheet's own per-row
    // block describe the same cell with the same formula on these templates.
    const dup = `${p.row}|${p.col}|${p.formula}|${p.fill}|${p.font}`;
    if (seen.has(dup)) continue;
    seen.add(dup);
    // Below the threshold every cell is its own group, so the loop below emits exactly
    // the per-cell rules this function has always produced.
    const key = mergeRuns
      ? `${p.col}|${p.fill}|${p.font}|${canonical(p.formula, p.row)}`
      : `${p.row}|${p.col}|${p.formula}`;
    (runs.get(key) ?? runs.set(key, []).get(key)!).push(p);
  }

  for (const group of runs.values()) {
    group.sort((a, b) => a.row - b.row);
    let i = 0;
    while (i < group.length) {
      let j = i;
      while (j + 1 < group.length && group[j + 1]!.row === group[j]!.row + 1) j++;   // contiguous run
      const head = group[i]!;
      try {
        let b = sheet.createConditionalFormattingRule()
          .whenFormulaSatisfied(`=${head.formula}`)
          .setRanges([{ startRow: head.row, endRow: group[j]!.row, startColumn: head.col, endColumn: head.col }]);
        if (head.font) b = b.setFontColor(head.font);
        if (head.fill) b = b.setBackground(head.fill);
        sheet.addConditionalFormattingRule(b.build());
        applied++;
        // DIAGNOSTIC (2026-08-13): which rule paints which cells. Kept because the
        // IsReport green fill was chased through two WRONG hypotheses — a bad column
        // shift, then Univer `SEARCH` semantics — both since disproved by
        // sdmsCfPredicate.engine.test.ts. Guessing cost more than logging.
        if (SDMS_CF_TRACE && head.fill)
          console.log(`[SDMS-CF] ${head.self}+${group[j]!.row - head.row} fill=${head.fill} font=${head.font ?? '-'} <= ${head.formula}`);
      } catch (e) {
        /* One bad rule must not cost the sheet its other colours — but it must not vanish
           either. This catch was SILENT, and `applied` counts only successes while the
           caller warns only when that count is ZERO, so a sheet where most rules threw
           looked exactly like one where they all worked. That is what hid the overflow
           above for weeks. */
        failed++;
        if (!firstError) firstError = { self: head.self, expr: head.formula, error: e };
      }
      i = j + 1;
    }
  }

  if (failed) {
    // eslint-disable-next-line no-console
    console.warn(
      `[SDMS-CF] ${failed} of ${applied + failed} conditional-format rule(s) were REJECTED by Univer — ` +
      `those cells render without their QC colour. First: ${firstError?.self} <= ${firstError?.expr}`,
      firstError?.error);
  }
  if (unsupportedTypesSeen.size) {
    // eslint-disable-next-line no-console
    console.warn(
      `[SDMS-CF] unsupported conditional-format rule type(s) NOT rendered: ${[...unsupportedTypesSeen].join(', ')} — ` +
      `those cells show no conditional colour. Add support in ruleFormula() if a template now relies on one.`);
  }
  return applied;
}

/**
 * Every Excel rule type in these templates expressed as ONE boolean formula, so a
 * single Univer builder (`whenFormulaSatisfied`) covers them all — that also keeps
 * the formula operands (VALUE(AB7), AD7) working, which the typed builders
 * (whenNumberLessThan(number)) cannot express.
 */
/**
 * Rewrite Excel's `containsText` idiom `NOT(ISERROR(SEARCH("x",REF)))` into
 * `IFERROR(SEARCH("x",REF),0)>0`, which is correct under BOTH engines' semantics.
 *
 * WHY (measured 2026-08-13, probe over the real template 8099): Excel makes `SEARCH`
 * raise `#VALUE!` when the needle is absent, so `NOT(ISERROR(...))` is FALSE.  Univer
 * does NOT error there, so `ISERROR` was false and `NOT(false)` came out TRUE —
 * the predicate was UNCONDITIONALLY true.  The template's QCStatus rule is authored
 * over `AE7:AJ7`, which spans AH (IsReport), so every IsReport cell got the "Pass"
 * GREEN FILL that OG does not show.  (The column/row offset was verified CORRECT —
 * the emitted expression really was `SEARCH("Pass",AH7)` — so the offset was never
 * the bug, despite an earlier diagnosis of mine that said so.)
 *
 * `IFERROR(SEARCH(...),0)>0` is FALSE both when SEARCH errors (Excel) and when it
 * returns 0/blank (Univer), and TRUE only on a real match at position >= 1.
 */
function robustContains(expr: string): string {
  return expr.replace(
    /NOT\s*\(\s*ISERROR\s*\(\s*SEARCH\s*\(([\s\S]*?)\)\s*\)\s*\)/gi,
    (_m, args: string) => `IFERROR(SEARCH(${args}),0)>0`,
  );
}

/* A PERCENT LITERAL is legal in an Excel rule operand and the SDMS templates use it
   heavily: the %Recovery bands are authored as `between 90% and 110%`, not
   `between 0.9 and 1.1`. Excel reads `90%` as 0.9; passing the token through and
   trusting Univer's parser to do the same is a dependency we do not need, and if it
   parsed it as 90 the band would be nonsense (nothing is between 90 and 110 when the
   cell holds 1.2). Fold it to its decimal value here so the comparison is plain
   arithmetic. Works inside a larger expression too - `AVERAGE(A1:A9)*90%` becomes
   `AVERAGE(A1:A9)*0.9`, which is the same number. (2026-08-24) */
function expandPercentLiterals(f: string): string {
  return f.replace(/(\d+(?:\.\d+)?)%/g, (_m, n: string) => String(Number(n) / 100));
}

function ruleFormula(rule: XlsxCfRule, self: string, k: number, dc = 0): string | null {
  const f = (i: number) => {
    const raw = rule.formulae?.[i];
    return raw
      ? expandPercentLiterals(shiftCols(shiftRows(String(raw).replace(/^=/, ''), k), dc))
      : null;
  };

  switch (rule.type) {
    case 'containsText': {
      // ExcelJS keeps the authored predicate in formulae[0]
      // (NOT(ISERROR(SEARCH("Fail",AE7)))) — reuse it row-shifted, else rebuild
      // from `text`.
      const own = f(0);
      if (own) return robustContains(own);
      return rule.text
        ? `IFERROR(SEARCH("${String(rule.text).replace(/^="|"$/g, '')}",${self}),0)>0`
        : null;
    }
    case 'cellIs': {
      const a = f(0); if (!a) return null;
      // TEXT vs NUMBER — Excel does NOT coerce, Univer does.
      //
      // Client 2026-08-20: the RL column was filled pink in Intelligence and left
      // white in ALPACA on QB26081709_074. The template really does put recovery
      // limits on that column — K carries `between -9999,80` -> pink — but the RL
      // cells are SHARED STRINGS (t="s", "0.009"), and in Excel a text value is
      // never "between" two numbers: text sorts ABOVE every number, so the upper
      // bound fails and the rule cannot fire. Univer's engine parses "0.009" as
      // 0.009 instead, so every RL cell matched.
      //
      // The converter is not at fault — numericValue() deliberately keeps a
      // numeric-looking STRING as text — so the fix belongs in the predicate:
      // state Excel's ordering explicitly. ISNUMBER gates the comparisons a text
      // cell must fail; ISTEXT short-circuits the ones it must pass, because
      // text > any number.
      //
      // Only for a NUMERIC criterion. A quoted criterion (cellIs = "Pass") is a
      // text comparison and is left exactly as it was.
      const numeric = !/^\s*"/.test(a);
      // "Takes part in a numeric comparison" = a NUMBER **or a truly EMPTY cell**.
      // Excel coerces an empty cell to 0, so `lessThan 60` DOES fire on a blank —
      // that is how ALPACA paints the empty Rec % / RPD % / IS-%-of-Avg cells pink on
      // QB26080509_042 (client 2026-08-21). Gating on ISNUMBER alone suppressed it and
      // lost that pink. `_LIMSEMPTY` (limsRoundFn.ts) is true for a genuinely empty cell AND for
      // the ""-constant the merge writes into a blank field (DevExpress paints those pink too —
      // OG's cells ARE ""-constants), never for "" returned by a formula — that is TEXT, and text
      // must still fail the comparison, which is what the RL fix earlier the same day was about.
      // (ISBLANK itself is DevExpress-faithful since 2026-09-24: a ""-constant is NOT blank.)
      const num = `OR(ISNUMBER(${self}),_LIMSEMPTY(${self}))`;
      const gate = (cmp: string) => (numeric ? `AND(${num},${cmp})` : cmp);
      const orText = (cmp: string) => (numeric ? `OR(ISTEXT(${self}),AND(${num},${cmp}))` : cmp);
      switch (rule.operator) {
        case 'equal':              return `${self}=${a}`;
        case 'notEqual':           return `${self}<>${a}`;
        case 'lessThan':           return gate(`${self}<${a}`);
        case 'lessThanOrEqual':    return gate(`${self}<=${a}`);
        case 'greaterThan':        return orText(`${self}>${a}`);
        case 'greaterThanOrEqual': return orText(`${self}>=${a}`);
        case 'between':  { const b = f(1); return b ? gate(`AND(${self}>=${a},${self}<=${b})`) : null; }
        case 'notBetween': {
          const b = f(1);
          return b ? (numeric
            ? `OR(ISTEXT(${self}),AND(${num},OR(${self}<${a},${self}>${b})))`
            : `OR(${self}<${a},${self}>${b})`) : null;
        }
        default: return null;
      }
    }
    case 'expression': return f(0);
    default:           return null;
  }
}
